import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { VariantTemplatePreviewModalProvider } from "./VariantTemplatePreviewModalContext";
import VariantTemplatePreviewModal from "./VariantTemplatePreviewModal";
import apiRequest from "../../utils/apiRequest";

const EVENT_NAME = "openVariantTemplatePreviewModal";

const VariantTemplatePreviewModalWrapper = (props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialRemixPrompt, setInitialRemixPrompt] = useState(null);

  const {
    website,
    cta,
    createVariantPath,
    environment,
    subscription,
    account,
    current_user,
    remix_mappings,
    styles = {},
  } = props;

  const [brandStyle, setBrandStyle] = useState({});
  const [, setFontsLoaded] = useState(0);

  useEffect(() => {
    if (modalOpen && !loading) {
      if (
        !window.builder_fonts_dropdown?.length &&
        typeof window.ctas?.builder?.fonts?.initialize === "function"
      ) {
        window.ctas.builder.fonts.initialize(() => {
          setFontsLoaded((n) => n + 1);
        });
      }
    }
  }, [modalOpen, loading]);

  useEffect(() => {
    const handleOpen = async (event) => {
      const {
        templateId,
        objectId,
        ctaType,
        brandStyle: eventBrandStyle,
        remixPrompt: eventRemixPrompt,
      } = event.detail || {};

      if (!templateId || !website || !cta || !createVariantPath) return;

      const urlMatch = window.location.pathname.match(/\/websites\/(\d+)/);
      const urlWebsiteId = urlMatch ? parseInt(urlMatch[1], 10) : null;
      if (urlWebsiteId && website.id && urlWebsiteId !== website.id) return;

      const remixPromptFromUrl =
        new URLSearchParams(window.location.search).get("remix_prompt") || "";
      const effectiveRemixPrompt =
        eventRemixPrompt || remixPromptFromUrl || null;

      setBrandStyle(eventBrandStyle || {});
      setInitialRemixPrompt(effectiveRemixPrompt);

      setLoading(true);
      setModalOpen(true);

      try {
        const response = await apiRequest(
          "GET",
          `${window.location.origin}/templates/${templateId}`,
          null,
          null,
          { Accept: "application/json" }
        );

        if (response?.code >= 200 && response?.code < 300 && response?.json) {
          setTemplateData(response.json);
        } else {
          setModalOpen(false);
        }
      } catch (err) {
        setModalOpen(false);
      } finally {
        setLoading(false);
      }
    };

    const handleOpenWithGuard = (event) => {
      event.stopImmediatePropagation();
      handleOpen(event);
    };
    document.addEventListener(EVENT_NAME, handleOpenWithGuard, true);
    return () =>
      document.removeEventListener(EVENT_NAME, handleOpenWithGuard, true);
  }, [website, cta, createVariantPath]);

  const handleClose = () => {
    setModalOpen(false);
    setTemplateData(null);
    setInitialRemixPrompt(null);
    if (typeof window.$ !== "undefined" && window.$("#newVariant").length) {
      window.$("#newVariant").modal("show");
    }
  };

  const handleVariantSuccess = (data) => {
    handleClose();
    if (data?.builder_url) {
      window.location.href = data.builder_url;
    } else if (data?.object?.id && cta?.id && website?.id) {
      window.location.href = `/websites/${website.id}/cta/${cta.id}/variants/${data.object.id}/edit`;
    }
  };

  const modalContent =
    modalOpen && templateData ? (
      <VariantTemplatePreviewModalProvider
        templateData={templateData}
        website={website}
        environment={environment}
        subscription={subscription}
        account={account}
        current_user={current_user}
        remix_mappings={remix_mappings}
        styles={{}}
      >
        <VariantTemplatePreviewModal
          templateData={templateData}
          onClose={handleClose}
          brandStyle={brandStyle}
          cta={cta}
          createVariantPath={createVariantPath}
          onSuccess={handleVariantSuccess}
          initialRemixPrompt={initialRemixPrompt}
        />
      </VariantTemplatePreviewModalProvider>
    ) : null;

  return (
    <>
      {modalOpen && loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <span className="cf-loading-spinner" />
        </div>
      )}
      {modalContent && createPortal(modalContent, document.body)}
    </>
  );
};

export default VariantTemplatePreviewModalWrapper;
export { EVENT_NAME };
